import React, { useState } from 'react';
import { Alert, Button, Dialog, Group } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useCurrentAgreement, useCurrentLocale } from '@economic/react-app';
import { Column, Row } from '~/app/shared/components/Grid/Grid';
import { BankDto } from '~/types/internal-api';
import { Account, ModalBaseProps } from '~/app/pages/accounting/bank/accounts/types';
import {
    CoAAlreadyUsed,
    getErrorResponse,
    getSelectedBank,
    onAccountNumberBlur,
    syncFields,
} from '~/app/pages/accounting/bank/accounts/utils';
import { Field, Form } from '~/app/shared/components/Form';
import { BankDropdown } from '~/app/pages/accounting/bank/accounts/components/BankDropdown';
import CurrencyDropdown from '~/app/shared/components/ui/CurrencyDropdown';
import { AccountFromCoADropdown } from '~/app/pages/accounting/bank/accounts/components/AccountFromCoADropdown';
import { validator } from '~/app/pages/accounting/bank/accounts/validator';

interface Props extends ModalBaseProps {
    addAccount: (account: Account) => Promise<any>;
    accounts: Account[];
    banks: BankDto[];
    open?: boolean;
    skipNotifications?: boolean;
    autoclose?: boolean;
}

export const AddModal = ({
    open,
    onClose,
    addAccount,
    accounts,
    banks,
    skipNotifications = true,
    autoclose,
    ...props
}: Props) => {
    const { features } = useCurrentAgreement();
    const { currencyCode } = useCurrentLocale();

    const [submitError, setSubmitError] = useState<string>();

    const { bankReconciliationForeignCurrency } = features;

    const onSubmit = (account: Account, closeDialog: () => void) => {
        if (CoAAlreadyUsed(account, accounts)) {
            setSubmitError(t`An account already exists with this Account from CoA`);
        } else {
            addAccount(account)
                .then(() => {
                    if (autoclose) {
                        closeDialog();
                    }
                })
                .catch(err => {
                    const { message } = getErrorResponse(err);

                    setSubmitError(message);
                });
        }
    };

    const onCloseLocal = () => {
        setSubmitError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={onCloseLocal} size="lg" {...props}>
            <Dialog.Content aria-label={t`Add bank account`}>
                {({ close }) => (
                    <>
                        <Dialog.Title>
                            <Trans>Add bank account</Trans>
                        </Dialog.Title>
                        <Form
                            onSubmit={account => onSubmit(account as any, close)}
                            validateOnBlur={false}
                            skipNotifications={skipNotifications}>
                            {({ handleSubmit, values, form }) => (
                                <>
                                    <Row divided>
                                        <Column xs="3">
                                            <Row>
                                                <Column>
                                                    <Field
                                                        as={BankDropdown}
                                                        label={t`Bank`}
                                                        name="bankName"
                                                        form={form}
                                                        required
                                                        validate={value => validator.bankName(value, banks)}
                                                    />
                                                    <Field.Hidden name="formatNumber" />
                                                </Column>
                                            </Row>
                                            <Row>
                                                {getSelectedBank(values.bankName, banks)?.validateDKNumberFormat ? (
                                                    <>
                                                        {/* we use duplicate {name}DK inputs as re-rendering with same name doesn't update validation properly */}
                                                        <Column xs="6">
                                                            <Field.Text
                                                                label={t`Sort code`}
                                                                name="bankRegistrationNumberDK"
                                                                defaultValue=""
                                                                onChange={({ target: { value } }) =>
                                                                    syncFields('bankRegistrationNumber', value, form)
                                                                }
                                                                validate={validator.danishBankRegistrationNumber}
                                                                required
                                                            />
                                                        </Column>
                                                        <Column xs="6">
                                                            <Field.Text
                                                                label={t`Account no.`}
                                                                name="bankAccountNumberDK"
                                                                defaultValue=""
                                                                onChange={({ target: { value } }) =>
                                                                    syncFields('bankAccountNumber', value, form)
                                                                }
                                                                onBlur={({ target: { value } }) =>
                                                                    onAccountNumberBlur(value, form)
                                                                }
                                                                validate={validator.danishBankAccountNumber}
                                                                required
                                                            />
                                                        </Column>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Column xs="6">
                                                            <Field.Text
                                                                label={t`Sort code`}
                                                                name="bankRegistrationNumber"
                                                                defaultValue=""
                                                                onChange={({ target: { value } }) =>
                                                                    syncFields('bankRegistrationNumber', value, form)
                                                                }
                                                                validate={validator.bankRegistrationNumber}
                                                            />
                                                        </Column>
                                                        <Column xs="6">
                                                            <Field.Text
                                                                label={t`Account no.`}
                                                                name="bankAccountNumber"
                                                                defaultValue=""
                                                                onChange={({ target: { value } }) =>
                                                                    syncFields('bankAccountNumber', value, form)
                                                                }
                                                                validate={validator.bankAccountNumber}
                                                            />
                                                        </Column>
                                                    </>
                                                )}
                                            </Row>
                                        </Column>
                                        <Column xs="3">
                                            <Row>
                                                <Column xs="6">
                                                    <Field.Text
                                                        disabled={getSelectedBank(values.bankName, banks)?.swift}
                                                        label={t`SWIFT code`}
                                                        name="bic"
                                                        defaultValue=""
                                                    />
                                                </Column>
                                                <Column xs="6">
                                                    <Field.Text
                                                        label={t`IBAN no.`}
                                                        name="iban"
                                                        defaultValue=""
                                                        validate={validator.iban}
                                                    />
                                                </Column>
                                            </Row>
                                            <Row>
                                                <Column xs="6">
                                                    <Field.Text
                                                        label={t`PBS no.`}
                                                        name="pbsNo"
                                                        defaultValue=""
                                                        validate={validator.pbsNo}
                                                    />
                                                </Column>
                                                <Column xs="6">
                                                    <Field.Number
                                                        label={t`PBS cust. gr. no.`}
                                                        name="pbsCustGrNo"
                                                        defaultValue={null}
                                                        validate={validator.pbsCustGrNo}
                                                    />
                                                </Column>
                                            </Row>
                                            {bankReconciliationForeignCurrency && (
                                                <Row>
                                                    <Column xs="6">
                                                        <Field
                                                            label={t`Currency`}
                                                            name="bankCurrency"
                                                            as={CurrencyDropdown}
                                                            defaultValue={currencyCode}
                                                        />
                                                    </Column>
                                                </Row>
                                            )}
                                            {values.bic === 'NDEADKKK' && (
                                                <Row>
                                                    <Column xs="12">
                                                        <Field.Text
                                                            label={t`Agreement no.`}
                                                            name="bankAgreementNumber"
                                                            defaultValue=""
                                                        />
                                                    </Column>
                                                </Row>
                                            )}
                                        </Column>
                                        <Column xs="3">
                                            <Row>
                                                <Column xs="6">
                                                    <Field.Text
                                                        label={t`Giro account`}
                                                        name="giroAccount"
                                                        defaultValue=""
                                                        validate={validator.giroAccount}
                                                    />
                                                </Column>
                                                <Column xs="6">
                                                    <Field.Text
                                                        label={t`FI supplier no.`}
                                                        name="fiSupplierNo"
                                                        defaultValue=""
                                                        validate={validator.fiSupplierNo}
                                                    />
                                                </Column>
                                            </Row>
                                            <Row>
                                                <Column>
                                                    <Field
                                                        as={AccountFromCoADropdown}
                                                        label={t`Account from CoA`}
                                                        name="number"
                                                        defaultValue={null}
                                                        required
                                                        validate={validator.number}
                                                    />
                                                </Column>
                                            </Row>
                                        </Column>
                                        <Column xs="3">
                                            <Row>
                                                <Column>
                                                    <Field.TextArea
                                                        label={t`Note`}
                                                        name="note"
                                                        defaultValue=""
                                                        validate={validator.note}
                                                    />
                                                </Column>
                                            </Row>
                                        </Column>
                                    </Row>
                                    <Dialog.Footer>
                                        <Group>
                                            <Dialog.Close>
                                                <Button>
                                                    <Trans>Cancel</Trans>
                                                </Button>
                                            </Dialog.Close>
                                            <Button appearance="primary" onClick={handleSubmit}>
                                                <Trans>Save</Trans>
                                            </Button>
                                        </Group>
                                    </Dialog.Footer>

                                    {submitError && (
                                        <Alert className="mt-4" state="error" title={t`Error`}>
                                            {submitError}
                                        </Alert>
                                    )}
                                </>
                            )}
                        </Form>
                    </>
                )}
            </Dialog.Content>
        </Dialog>
    );
};
